<template>
	<v-row>
		<v-col>
			<v-form v-model="formIsValid" ref="settingsForm">

				<ui-box
						:form="$refs.settingsForm"
						new-forms
				>

					<form-title title="Channel Images">
						Updating the images below will only affect the images for this channel.
						Global settings will not be updated. If you wish to use the global partner
						level images, no changes are needed.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<form-subsection
								caption="Recommended minimum size: 256 x 256 @ 2X"
								title="Add to Homescreen Icon"
							>
								Used where there is both vertical and horizontal space for clearly
								displaying your logo.
							</form-subsection>
							<v-row>
								<v-col>
									<v-row>
										<bw-image
												:aspect-ratio="calculateAR(mediaSettings.logo.cropperSettings.limitAR)"
												:cols="{lg: 6, md: 8, sm: 10}"
												cover
												hide-edit
												label="Default From Partner"
												:upload-assignment="'partner'"
												:upload-assignment-id="partner.id"
												:value="partnerLogoImage"
										/>
									</v-row>
								</v-col>
								<v-col>
									<v-row class="h-100">
										<bw-image
												v-model="logoImage"
												:aspect-ratio="calculateAR(mediaSettings.logo.cropperSettings.limitAR)"
												:cols="{lg: 6, md: 8, sm: 10}"
												cover
												label="Channel Override"
												showRemove
												:upload-assignment="'offer'"
												:upload-assignment-id="localChannel.id"
												@delete="logoImage = null"
										/>
									</v-row>
								</v-col>
							</v-row>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<form-subsection
								caption="Recommended minimum size: 256 x 64 @ 2X"
								title="Horizontal Logo"
							>
								Used mainly as the mobile pass header logo.
							</form-subsection>
							<v-row>
								<v-col>
									<v-row>
										<bw-image
												:aspect-ratio="4"
												:cols="{lg: 6, md: 8, sm: 10}"
												contain
												hide-edit
												label="Default From Partner"
												:upload-assignment="'offer'"
												:upload-assignment-id="localChannel.id"
												:value="partnerHorizontalLogoImage"
										/>
									</v-row>
								</v-col>
								<v-col>
									<v-row class="h-100">
										<bw-image
												v-model="horizontalLogoImage"
												:aspect-ratio="4"
												:cols="{lg: 6, md: 8, sm: 10}"
												contain
												label="Channel Override"
												showRemove
												:upload-assignment="'offer'"
												:upload-assignment-id="localChannel.id"
												@delete="horizontalLogoImage = null"
										/>
									</v-row>
								</v-col>
							</v-row>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<form-subsection
								caption="Recommended minimum size: 800 x 1200 @ 2X"
								title="Login/Experiences Background"
							>
								Seen in the mobile pass Login and Experience pages.
							</form-subsection>
							<v-row>
								<v-col>
									<v-row>
										<bw-image
												:aspect-ratio="calculateAR(mediaSettings.login.cropperSettings.limitAR)"
												:cols="{lg: 6, md: 8, sm: 10}"
												cover
												hide-edit
												label="Default From Partner"
												:upload-assignment="'offer'"
												:upload-assignment-id="localChannel.id"
												:value="partnerLoginImage"
										/>
									</v-row>
								</v-col>
								<v-col>
									<v-row class="h-100">
										<bw-image
												v-model="loginImage"
												:aspect-ratio="calculateAR(mediaSettings.login.cropperSettings.limitAR)"
												:cols="{lg: 6, md: 8, sm: 10}"
												cover
												label="Channel Override"
												showRemove
												:upload-assignment="'offer'"
												:upload-assignment-id="localChannel.id"
												@delete="loginImage = null"
										/>
									</v-row>
								</v-col>
							</v-row>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<form-subsection
								caption="Recommended minimum size: 1260 x 560 @ 2X"
								title="Email"
							>
								This image will be used as the header background in emails sent within
								Bandwango on your behalf.
							</form-subsection>
							<v-row>
								<v-col>
									<v-row>
										<bw-image
												:aspect-ratio="calculateAR(mediaSettings.email.cropperSettings.limitAR)"
												:cols="{lg: 6, md: 8, sm: 10}"
												cover
												hide-edit
												label="Default From Partner"
												:upload-assignment="'offer'"
												:upload-assignment-id="localChannel.id"
												:value="partnerEmailImage"
										/>
									</v-row>
								</v-col>
								<v-col>
									<v-row class="h-100">
										<bw-image
												v-model="emailImage"
												:aspect-ratio="calculateAR(mediaSettings.email.cropperSettings.limitAR)"
												:cols="{lg: 6, md: 8, sm: 10}"
												cover
												label="Channel Override"
												showRemove
												:upload-assignment="'offer'"
												:upload-assignment-id="localChannel.id"
												@delete="emailImage = null"
										/>
									</v-row>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</ui-box>

				<form-divider/>

				<ui-box
						:form="$refs.settingsForm"
						new-forms
				>
					<form-title title="Channel Primary Colors">
						Updating the Primary color below will only affect the color for this channel.
						Global settings will not be updated. If you wish to use the global partner
						level Primary colors, no changes are needed.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" md="6">

							<h6 class="font-weight-medium text-body-2">Default From Partner</h6>
							<v-divider class="mb-2"/>

							<v-row>

								<!-- Color Text Input + Button / Dialog -->
								<v-col md="6" sm="6">
									<v-row no-gutters>
										<v-col>
											<v-text-field
													class="rounded-lg"
													disabled
													hide-details="auto"
													label="Primary Color"
													outlined
													readonly
													:value="partnerPrimaryColor"
											></v-text-field>
										</v-col>
									</v-row>
								</v-col>
								<!-- Color Text Input + Button / Dialog - END -->

								<!-- Button Previews -->
								<v-col cols="auto" sm="6" md="6">
									<div class="d-flex">
										<v-btn
												class="px-1 px-sm-2 rounded-lg"
												:color="partnerPrimaryColor"
												depressed
												:min-width="$vuetify.breakpoint.xsOnly
																? 48
																: ($vuetify.breakpoint.smOnly
																		? 64
																		: 92
																)"
												outlined
												x-large
										>
											<template v-if="$vuetify.breakpoint.xsOnly">B</template>
											<template v-else-if="$vuetify.breakpoint.smOnly">BTN</template>
											<template v-else>BUTTON</template>
										</v-btn>
										<v-btn
												class="px-1 px-sm-2 ms-2 rounded-lg"
												:color="partnerPrimaryColor"
												depressed
												:min-width="$vuetify.breakpoint.xsOnly
																? 48
																: ($vuetify.breakpoint.smOnly
																		? 64
																		: 92
																)"
												:style="{ color: partnerBackgroundText }"
												x-large
										>
											<template v-if="$vuetify.breakpoint.xsOnly">B</template>
											<template v-else-if="$vuetify.breakpoint.smOnly">BTN</template>
											<template v-else>BUTTON</template>
										</v-btn>
									</div>
								</v-col>
								<!-- Button Previews - END -->

							</v-row>
						</v-col>
						<v-col cols="12" md="6">

							<h6 class="font-weight-medium text-body-2">Channel Override</h6>
							<v-divider class="mb-2"/>

							<v-row>

								<!-- Color Text Input + Button / Dialog -->
								<v-col md="6" sm="6">
									<v-row no-gutters>
										<v-col>
											<v-text-field
													v-model="tempPrimaryColor"
													class="rounded-l-lg rounded-r-0"
													hide-details="auto"
													label="Primary Color"
													outlined
													:rules="rules.hexColor"
													@change="updatePrimaryColor"
													@blur="updatePrimaryColor"
											></v-text-field>
										</v-col>
										<v-col cols="auto">

											<v-dialog
													v-model="primaryColorMenu"
													content-class="rounded-xl"
													persistent
													scrollable
													width="400px"
											>

												<template v-slot:activator="{ on }">
													<v-btn
															v-on="on"
															class="px-2 rounded-l-0 rounded-r-lg"
															color="secondary"
															depressed
															outlined
															style="border-left: none; min-width: 56px;"
															x-large
													>
														<v-icon class="mx-0">mdi-palette</v-icon>
													</v-btn>
												</template>

												<v-card class="position-relative" rounded="xl">

													<v-card-title
															class="d-flex justify-space-between ma-0 ma-md-4">
														<div class="w-100">
															<div
																	class="align-center d-flex justify-space-between">
																<h6>Primary Color</h6>
																<v-btn
																		class="mt-n1 me-n2"
																		color="black"
																		depressed
																		icon
																		@click="cancelPrimaryColor"
																>
																	<v-icon>mdi-close</v-icon>
																</v-btn>
															</div>
														</div>
													</v-card-title>

													<v-card-text class="text-body-2">
														<v-color-picker
																v-model="primaryColorPicked"
																ref="colorPicker"
																class="rounded-lg"
																dot-size="20"
																@input="(e) => tempPrimaryColor = e"
														/>
													</v-card-text>

													<v-card-actions>
														<div class="w-100">

															<div
																	class="align-center d-flex justify-space-between">
																<span></span>
																<div class="d-flex justify-end">
																	<v-btn
																			class="me-2 me-md-4 rounded-lg"
																			color="red darken-1"
																			depressed
																			:large="$vuetify.breakpoint.mdAndUp"
																			outlined
																			@click="resetPrimaryColor"
																	>
																		Reset
																	</v-btn>
																	<v-btn
																			class="me-2 me-md-4 rounded-lg"
																			color="grey darken-1"
																			depressed
																			:large="$vuetify.breakpoint.mdAndUp"
																			outlined
																			@click="cancelPrimaryColor"
																	>
																		Cancel
																	</v-btn>
																	<v-btn
																			class="rounded-lg"
																			color="primary"
																			depressed
																			:large="$vuetify.breakpoint.mdAndUp"
																			@click="handleAcceptPrimaryColor"
																	>
																		Accept
																	</v-btn>
																</div>
															</div>
														</div>
													</v-card-actions>
												</v-card>
											</v-dialog>

										</v-col>
									</v-row>
								</v-col>
								<!-- Color Text Input + Button / Dialog - END -->

								<!-- Button Previews -->
								<v-col
										v-if="localChannel.styles.colors.primary.main"
										cols="auto"
										sm="6"
										md="6"
								>
									<div class="d-flex">
										<v-btn
												class="px-1 px-sm-2 rounded-lg"
												:color="tempPrimaryColor"
												depressed
												:min-width="$vuetify.breakpoint.xsOnly
																? 48
																: ($vuetify.breakpoint.smOnly
																		? 64
																		: 92
																)"
												outlined
												x-large
										>
											<template v-if="$vuetify.breakpoint.xsOnly">B</template>
											<template v-else-if="$vuetify.breakpoint.smOnly">BTN</template>
											<template v-else>BUTTON</template>
										</v-btn>
										<v-btn
												class="px-1 px-sm-2 ms-2 rounded-lg"
												:color="tempPrimaryColor"
												depressed
												:min-width="$vuetify.breakpoint.xsOnly
																? 48
																: ($vuetify.breakpoint.smOnly
																		? 64
																		: 92
																)"
												:style="{ color: textColor }"
												x-large
										>
											<template v-if="$vuetify.breakpoint.xsOnly">B</template>
											<template v-else-if="$vuetify.breakpoint.smOnly">BTN</template>
											<template v-else>BUTTON</template>
										</v-btn>
									</div>
								</v-col>
								<!-- Button Previews - END -->

							</v-row>
						</v-col>
					</v-row>
				</ui-box>

				<form-divider/>

				<ui-box
						:form="$refs.settingsForm"
						new-forms
				>

					<form-title title="Legal Information and Policies">
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<v-textarea
									v-model="localChannel.terms_conditions"
									auto-grow
									class="rounded-lg"
									label="Terms & Conditions"
									outlined
									rows="10"
							></v-textarea>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-textarea
									v-model="localChannel.privacy_policy"
									auto-grow
									class="rounded-lg"
									label="Privacy Policy"
									outlined
									rows="4"
							></v-textarea>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-textarea
								v-model="localChannel.refund_policy"
								auto-grow
								class="rounded-lg"
								label="Refund Policy"
								outlined
								rows="10"
							></v-textarea>
						</v-col>
					</v-row>
				</ui-box>

				<form-divider/>

				<!-- This section remainds hidden for now -->
				<ui-box
						v-if="false"
						:form="$refs.settingsForm"
						new-forms
				>

					<form-title title="Localization">
						For translations, Bandwango implements the Bablic localization plugin. If you
						are using translations you must place the plugin <code>&lt;script&gt;</code>
						tag in the <code>&lt;head&gt;</code> of your document. And then provide the
						translations below.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<div class="field-actions d-flex justify-end">
								<v-btn icon @click="copyToClipboard(bablicScript)">
									<v-icon color="secondary">mdi-content-copy</v-icon>
								</v-btn>
							</div>
							<v-text-field
									v-model="bablicScript"
									class="rounded-l-lg rounded-r-0"
									hide-details="auto"
									label="Bablic Script"
									outlined
									readonly
									type="text"
							></v-text-field>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-textarea
									v-model="localChannel.bablic_snippet"
									class="rounded-lg"
									clearable
									hide-details="auto"
									label="Bablic Translations"
									outlined
									rows="4"
									type="text"
							></v-textarea>
						</v-col>
					</v-row>
				</ui-box>
				<!-- This section remainds hidden for now -->
				<form-divider v-if="false"/>


				<ui-box
						:form="$refs.settingsForm"
						new-forms
				>
					<form-title title="Sales Tax">
						This is the sales tax that will be charged to the customer whenever they
						purchase anything through this channel. This tax will apply to all paid items
						in the channel. You can override this tax rate on an individual item level as
						well. However, if the tax rate is overridden on the individual item, this tax
						rate will no longer apply
					</form-title>

					<form-divider inner/>
					<v-row>
						<v-col cols="12" sm="9" md="6">
							<v-text-field
									v-model.number="localChannel.tax_rate"
									class="mt-4 mt-sm-0 rounded-lg"
									hide-details="auto"
									label="Sales Tax Rate"
									outlined
									suffix=" %"
									type="number"
									@wheel.native.prevent
							></v-text-field>
						</v-col>
					</v-row>
				</ui-box>

				<ui-box
						:form="$refs.settingsForm"
						new-forms
				>
					<form-title title="Convenience Fee">
						You can add a convenience fee to all sales within this channel. Please note
						that if you have also defined a convenience fee on any individual item that
						is sold in this channel, the (2) convenience fees will be <em>added together
						upon checkout</em>.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" sm="9" md="6">
							<v-row>
								<v-col cols="4">
									<v-select
											v-model="localChannel.convenience_fee_type"
											class="rounded-lg"
											hide-details="auto"
											:items="[{name: '$', value: 'fixed'}, {name: '%', value: 'percent'}]"
											item-text="name"
											label="Convenience Fee Type"
											outlined
											width="80"
									></v-select>
								</v-col>
								<v-col cols="8">
									<v-text-field
											v-model.number="localChannel.convenience_fee"
											class="rounded-lg"
											hide-details="auto"
											label="Convenience Fee"
											outlined
											:prefix="localChannel.convenience_fee_type === 'fixed' ? '$ ' : null"
											:suffix="localChannel.convenience_fee_type === 'percent' ? ' %' : null"
											type="number"
											@wheel.native.prevent
									></v-text-field>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</ui-box>

				<save-form
						:refs="$refs"
						:is-saving="isSaving"
						:is-valid="formIsValid"
						newForms
						save-text="Save"
						@cancel-action="cancelChanges"
						@save-action="saveChanges"
				></save-form>

			</v-form>
		</v-col>
	</v-row>
</template>

<script>

import {calculateAR, copyToClipboard, helpers} from '../../../../Shared/helpers';

export default {
	name: 'ChannelSettings',
	props: {
		channel: {
			type: Object,
			default: () => ({})
		},
		partner: {
			type: Object | null,
			default: null
		},
		returnUrl: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			bablicScript: '<script src="//d.bablic.com/snippet/5e8f8593c8ff6000013f5b97.js?version=3.9"><\/script>',
			formIsValid: false,
			isSaving: false,
			localChannel: {},
			mediaSettings: {
				logo: { cropperSettings: { limitAR: '1:1', x: 0, y: 0 }, name: 'Logo' },
				horizontalLogo: {
					cropperSettings: { limitAR: 'NaN', x: 0, y: 0 },
					name: 'Horizontal Logo'
				},
				login: {
					cropperSettings: { limitAR: '2:3', x: 0, y: 0 },
					name: 'Login/Experiences Background'
				},
				email: { cropperSettings: { limitAR: '125:56', x: 0, y: 0 }, name: 'Email' }
			},
			partnerPrimaryColor: '#2a73c5',
			partnerBackgroundText: '#ffffff',
			primaryColorMenu: false,
			primaryColorPicked: null,
			primaryColor: null,
			rules: {
				hexColor: [v => !v || /^#[0-9A-F]{6}$/i.test(v) || 'Invalid HEX color (e.g. #2a73c5)']
			},
			tempPrimaryColor: null,
			textColor: null
		}
	},
	created() {
		this.localChannel = cloneDeep(this.channel);
		this.setDefaultValues(this.localChannel);
	},
	mounted() {
		const setColor = this.localChannel.styles.colors.primary.main || '#2a73c5';
		const setTextColor = this.localChannel.styles.colors.primary.backgroundText || '#ffffff';
		this.partnerPrimaryColor = this.partner.styles?.colors?.primary?.main || '#2a73c5';
		this.partnerBackgroundText  = this.partner.styles?.colors?.primary?.backgroundText || '#ffffff';
		this.primaryColor = setColor;
		this.primaryColorPicked = setColor;
		this.tempPrimaryColor = setColor;
		this.textColor = setTextColor;
	},
	computed: {
		// I don't think we want to make them add all overrides???
		hasAllImages() {
			const { offer } = this.images;
			const locations = [
				offer.logo,
				offer.horizontalLogo,
				offer.login,
				offer.email
			];

			//const locations = [this.logo, this.horizontalLogo, this.login, this.email];
			const images = locations
					.filter(i => !i.url.startsWith('https://via.placehold.co'));
			return images.length === locations.length;
		},
		partnerLogoImage() {
			return this.findMedia(this.partner.media, 'Logo', this.mediaSettings.logo);
		},
		logoImage: {
			get() {
				return this.findMedia(this.localChannel.media, 'Logo', this.mediaSettings.logo);
			},
			set(v) {
				const location = 'Logo';
				if(v === null) {
					this.removeMedia(location);
					this.removeMedia('Icon152');
					this.removeMedia('Icon196');
					return;
				}
				v.location = location;
				let logoIndex = this.localChannel.media.findIndex(x => x.location === v.location);
				if(logoIndex > -1) {
					const id = this.localChannel.media[logoIndex].id;
					this.$set(this.localChannel.media, logoIndex, { ...v, id });
				}
				else {
					this.localChannel.media.push(v);
				}

				const createIcon = (size) => {
					const icon = { ...v, location: `Icon${size}` };
					icon.scale_y = icon.cropperSettings.scale_y = icon.scale_x = icon.cropperSettings.scale_x = size / 250;
					let iconIndex = this.localChannel.media.findIndex(x => x.location === icon.location);
					if(iconIndex > -1) {
						const id = this.localChannel.media[iconIndex].id;
						this.$set(this.localChannel.media, iconIndex, { ...icon, id });
					}
					else {
						this.localChannel.media.push(icon);
					}
				};
				createIcon(152);
				createIcon(196);
			}
		},
		partnerHorizontalLogoImage() {
			return this.findMedia(this.partner.media, 'HorizontalLogo', this.mediaSettings.horizontalLogo);
		},
		horizontalLogoImage: {
			get() {
				return this.findMedia(this.localChannel.media, 'HorizontalLogo', this.mediaSettings.horizontalLogo);
			},
			set(v) {
				const location = 'HorizontalLogo';
				if(v === null) {
					this.removeMedia(location);
					return;
				}
				v.location = location;
				let i = this.localChannel.media.findIndex(x => x.location === v.location);
				if(i > -1) {
					this.$set(this.localChannel.media, i, v);
				}
				else {
					this.localChannel.media.push(v);
				}
			}
		},
		partnerLoginImage() {
			return this.findMedia(this.partner.media, 'Login', this.mediaSettings.login);
		},
		loginImage: {
			get() {
				return this.findMedia(this.localChannel.media, 'Login', this.mediaSettings.login);
			},
			set(v) {
				const location = 'Login';
				if(v === null) {
					this.removeMedia(location);
					return;
				}
				v.location = location;
				let i = this.localChannel.media.findIndex(x => x.location === v.location);
				if(i > -1) {
					this.$set(this.localChannel.media, i, v);
				}
				else {
					this.localChannel.media.push(v);
				}
			}
		},
		partnerEmailImage() {
			return this.findMedia(this.partner.media, 'Email', this.mediaSettings.email);
		},
		emailImage: {
			get() {
				return this.findMedia(this.localChannel.media, 'Email', this.mediaSettings.email);
			},
			set(v) {
				const location = 'Email';
				if(v === null) {
					this.removeMedia(location);
					return;
				}
				v.location = location;
				let i = this.localChannel.media.findIndex(x => x.location === v.location);
				if(i > -1) {
					this.$set(this.localChannel.media, i, v);
				}
				else {
					this.localChannel.media.push(v);
				}
			}
		}
	},
	methods: {
		copyToClipboard,
		calculateAR,
		cancelPrimaryColor() {
			this.primaryColorMenu = false;
			this.updateTextColor(this.primaryColor);
			this.tempPrimaryColor = this.localChannel.styles.colors.primary.main;
		},
		resetPrimaryColor() {
			this.primaryColorMenu = false;
			this.textColor = null;
			this.tempPrimaryColor = null;
			this.localChannel.styles.colors.primary.main = null;
			this.localChannel.styles.colors.primary.backgroundText = null;
		},
		updatePrimaryColor() {
			this.primaryColorPicked = this.tempPrimaryColor;
			this.localChannel.styles.colors.primary.main = this.tempPrimaryColor;
			this.localChannel.styles.colors.primary.backgroundText = this.primaryColor !== null
					? helpers.getContrastTextColor(this.primaryColor)
					: null;
		},
		updateTextColor(color) {
			this.textColor = color !== null ? helpers.getContrastTextColor(color) : null;
		},
		handleAcceptPrimaryColor() {
			this.primaryColorMenu = false;
			this.updatePrimaryColor();
		},
		cancelChanges() {
			this.doCancelChanges();
		},
		doCancelChanges() {
			window.location = this.getReturnUrl();
		},
		findMedia(mediaArray, location, defaultSettings) {
			let media = mediaArray.find(x => x.location === location);
			const { cropperSettings, name } = defaultSettings;

			if(!media?.url) {
				media = {
					location,
					name,
					url: '/images/placehold.co.png',
					cropperSettings
				};
			}
			else {
				media.cropperSettings = cropperSettings;
			}
			return media;
		},
		getReturnUrl() {
			return `/partner-portal/${this.partner.id}/channels/${this.localChannel.id}/communication`;
		},

		handleUpdate(mediaArray, mediaValue, location) {
			if(mediaValue === null) {
				this.removeMedia(mediaArray, location);
				return;
			}
			mediaValue.location = location;
			let index = mediaArray.findIndex(x => x.location === location);
			if(index > -1) {
				this.$set(mediaArray, index, mediaValue);
			}
			else {
				this.$set(mediaArray, mediaArray.length, mediaValue);
			}
		},

		removeMedia(location) {
			const mediaArray = this.localChannel.media;
			let index = mediaArray.findIndex(x => x.location === location);

			// If there's no ID we just remove it from the array, nothing else to do
			if(!mediaArray[index]?.id) this.$delete(mediaArray, index);

			// If we find the media by index and we have an ID let's set it for removal
			if(index > -1) {
				this.$set(mediaArray, index, {
					flag: 'delete',
					id: mediaArray[index].id,
					location: mediaArray[index].location
				});
			}
		},

		setDefaultValues(channel) {
			channel.terms_conditions = channel.terms_conditions || channel.const.DEFAULT_TERMS_CONDITIONS;
			channel.privacy_policy = channel.privacy_policy || channel.const.DEFAULT_PRIVACY_POLICY;
			channel.refund_policy = channel.refund_policy || channel.const.DEFAULT_REFUND_POLICY;
			channel.styles = channel.styles || { colors: { primary: {} } };

			const defaultQuestions = {
				'how-to-add-pass-to-my-phone': { 'question': 'How do I add the pass to my phone?' },
				'how-to-enable-location-services': { 'question': 'How do I enable location services?' },
			};
		},
		saveChanges() {
			// Validate first...
			this.$refs.settingsForm.validate();
			if(!this.formIsValid) return;

			this.isSaving = true;

			let url = `/partner-portal/${this.partner.id}/channels/save-channel/${this.localChannel.id || ''}`;
			axios.put(url, this.localChannel).then(response => {
				this.localChannel.id = response.data.channel.id;
				window.EventBus.$emit('system-messages', [
					{
						message: this.localChannel.name + ' ' + (this.localChannel.id
								? 'updated'
								: 'created') + ' - redirecting now...',
						messageType: 'success',
						showOverlay: true
					}
				]);
				setTimeout(() => {
					window.location = this.getReturnUrl();
				}, 2000);
			}).catch(error => {
				this.isSaving = false;
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			});
		}
	},
	watch: {
		'localChannel.convenience_fee_type': function (newVal) {
			if(newVal === 'fixed' && this.localChannel.convenience_fee === '0')
				this.localChannel.convenience_fee = '0.00';
			if(newVal === 'percent' && this.localChannel.convenience_fee === '0.00')
				this.localChannel.convenience_fee = '0';
		},
		tempPrimaryColor: function (newVal) {
			this.updateTextColor(newVal);
		}
	}
};

</script>

<style lang="scss"></style>
